import React from "react";
import { Button, Icon, Segment, Step } from "semantic-ui-react";
import { RRCProps, Reducers, ReduxReusableComponents, State } from "@crispico/foundation-react/reduxReusableComponents/ReduxReusableComponents";
import { createTestids } from "@famiprog-foundation/tests-are-demo";
import { ScriptableContainer, Scriptable } from "@famiprog-foundation/scriptable-ui";

export const wizardTestids = createTestids("Wizard", {
    step: "", next: "", finish: "",
});

export interface WizardStep {
    title: String;
    description?: String;
    icon: String;
    render: () => React.ReactNode;
    onPreviousClick?: () => boolean | Promise<boolean>;
    onNextClick?: () => boolean | Promise<boolean>;
    previousDisabled?: () => boolean;
    nextDisabled?: () => boolean;
}

export class WizardState extends State {
    currentStepIndex = 0;
}

export class WizardReducers<S extends WizardState = WizardState> extends Reducers<S> {
    reset() {
        this.s.currentStepIndex = 0;
    }
}

type Props = RRCProps<WizardState, WizardReducers> & {
    steps: Array<WizardStep>;
    onFinishClick?: () => void;
    onCancelClick?: () => void;
};

export class Wizard extends React.Component<Props> {

    public getCurrentStepIndex() {
        return this.props.s.currentStepIndex;
    }

    async back(currentStepIndex: number, steps: Array<WizardStep>) {
        if (currentStepIndex > 0) {
            const currentStep = steps[currentStepIndex];
            if (currentStep.onPreviousClick) {
                const result = await currentStep.onPreviousClick.apply(null);
                if (!result) {
                    return;
                }
            }
            this.props.r.setInReduxState({ currentStepIndex: currentStepIndex - 1 });
        }
    }

    async next(currentStepIndex: number, steps: Array<WizardStep>) {
        if (currentStepIndex < steps.length - 1) {
            const currentStep = steps[currentStepIndex];
            if (currentStep.onNextClick) {
                const result = await currentStep.onNextClick.apply(null);
                if (!result) {
                    return;
                }
            }
            this.props.r.setInReduxState({ currentStepIndex: currentStepIndex + 1 });
        }
    }

    render() {
        const { props } = this;
        return <ScriptableContainer id="Wizard">
            <>
                <Step.Group widths={props.steps.length as any} size="small">
                    {props.steps.map((step, index) => (
                        <Step data-cy={"step" + index} data-testid={wizardTestids.step + "_" + index} active={index === props.s.currentStepIndex} key={"step" + index}>
                            {step.icon && <Icon name={step.icon as any} />}
                            <Step.Content>
                                <Step.Title>{step.title}</Step.Title>
                                <Step.Description>{step.description}</Step.Description>
                            </Step.Content>
                        </Step>
                    ))}
                </Step.Group>
                {props.s.currentStepIndex >= 0 && props.steps[props.s.currentStepIndex].render?.apply(null)}
                <Segment className="buttonBar">
                    <Scriptable id="PreviousButton" onClick={async () => await this.back(props.s.currentStepIndex, props.steps)}>{scriptable =>
                        <Button data-cy={"prevBtn"} disabled={props.s.currentStepIndex === 0 || props.steps[props.s.currentStepIndex].previousDisabled?.()} onClick={() => scriptable.proxy.onClick()}>
                            <Icon name='arrow left' />{_msg("Wizard.previous")}
                        </Button>
                    }</Scriptable>
                    <Scriptable id="NextButton" onClick={async () => await this.next(props.s.currentStepIndex, props.steps)}>{scriptable =>
                        <Button data-cy={"nextBtn"} data-testid={wizardTestids.next} disabled={props.s.currentStepIndex === props.steps.length - 1 || props.steps[props.s.currentStepIndex].nextDisabled?.()} onClick={() => scriptable.proxy.onClick()}>
                            {_msg("Wizard.next")}<Icon name='arrow right' />
                        </Button>
                    }</Scriptable>
                    <Scriptable id="FinishButton" onClick={() => props.onFinishClick?.apply(null)}>{scriptable =>
                        <Button data-cy={"finishBtn"} data-testid={wizardTestids.finish} disabled={props.s.currentStepIndex !== props.steps.length - 1} onClick={() => scriptable.proxy.onClick()}>
                            {_msg("Wizard.finish")}
                        </Button>
                    }</Scriptable>
                    <Scriptable id="CancelButton" onClick={() => props.onCancelClick?.apply(null)}>{scriptable =>
                        <Button data-cy={"cancelBtn"} onClick={() => scriptable.proxy.onClick()}>
                            {_msg("general.cancel")}
                        </Button>
                    }</Scriptable>
                </Segment>
            </>
        </ScriptableContainer>;
    }
}

export const WizardRRC = ReduxReusableComponents.connectRRC(WizardState, WizardReducers, Wizard);


"../../reduxReusableComponents/ReduxReusableComponents"